<template>
  <div class="teste-agentes">
    <header class="ta-header" :style="headerStyle">
      <div class="ta-header-inner">
        <span class="ta-badge">Ambiente de teste</span>
        <h1>Teste de Agentes AUC</h1>

        <p v-if="loading" class="ta-campaign">Identificando a campanha…</p>

        <template v-else>
          <p v-if="campaignName" class="ta-campaign">
            Campanha: <strong>{{ campaignName }}</strong>
            <span class="ta-tag" :class="isIncentivada ? 'is-incentivada' : 'is-nao'">
              {{ isIncentivada ? "Incentivada" : "Não incentivada" }}
            </span>
          </p>
          <p v-else class="ta-warn">
            Campanha não identificada. Esta página é acessada pelo
            <strong>subdomínio da campanha</strong>
            (ex.: <code>osesp.abraceumacausa.com.br/teste-agentes</code>). Para
            testar localmente, acesse por <code>osesp.localhost:8080/teste-agentes</code>.
          </p>

          <ul class="ta-status">
            <li>
              <span class="dot on"></span>
              Chat flutuante: <strong>ativo</strong> (canto inferior direito)
            </li>
            <li>
              <span class="dot" :class="isIncentivada ? 'on' : 'off'"></span>
              Simulador:
              <strong>{{ isIncentivada ? "ativo (abaixo)" : "não carregado — campanha não incentivada" }}</strong>
            </li>
          </ul>
        </template>
      </div>
    </header>

    <main class="ta-main">
      <!-- Simulador só é carregado para campanhas INCENTIVADAS -->
      <section v-if="isIncentivada" class="ta-sim">
        <h2>Simulador de doação incentivada</h2>
        <p class="ta-sim-hint">
          Descubra o potencial de doação pela declaração completa do IRPF.
        </p>
        <!-- Container do simulador inline (widget AUC auto-hospedado em /vendor) -->
        <div id="auc-simulador-inline" class="sim-inline-wrap"></div>
      </section>

      <section v-else-if="!loading && campaignName" class="ta-only-chat">
        <p>
          Esta campanha é <strong>não incentivada</strong> — apenas o
          <strong>chat flutuante</strong> é carregado nesta página.
        </p>
      </section>
    </main>
  </div>
</template>

<script type="plain/text">
import CampaignService from "@/scripts/services/campaign.service";
import CommonHelper from "@/scripts/helpers/common.helper";
import { CampaignTypes } from "@/scripts/models/enums/campaignTypes.enum";

// Página de teste dos "agentes" (chat flutuante + simulador). Carrega o SIMULADOR e o
// CHAT quando a campanha é INCENTIVADA; carrega apenas o CHAT quando NÃO incentivada.
// A campanha é identificada EXCLUSIVAMENTE pelo SUBDOMÍNIO acessado
// (CommonHelper.getSubdomain()); o tipo (incentivada/não) e as cores vêm da própria
// configuração da campanha — nada é definido manualmente.
export default {
  data() {
    return {
      campaignService: new CampaignService(),
      loading: true,
      slug: null,
      campaignName: null,
      isIncentivada: false,
      // cores da campanha (hex) repassadas aos dois widgets; null = usa o default do widget
      color: null,
      colorSecondary: null
    };
  },
  computed: {
    headerStyle() {
      if (!this.color) return null;
      const to = this.colorSecondary || this.color;
      return {
        background: "linear-gradient(135deg, " + this.color + " 0%, " + to + " 100%)"
      };
    }
  },
  async created() {
    // Identificação pelo subdomínio acessado (produção/homolog e também local via
    // <slug>.localhost — a API resolve a campanha pelo header Referer).
    this.slug = CommonHelper.getSubdomain();

    if (this.slug) {
      try {
        const config = await this.campaignService.getCampaignLandingPageConfigBySubdomain();
        if (config) {
          if (config.campaign) {
            this.campaignName =
              config.campaign.name || config.campaign.title || this.slug;
            // Tipo vem direto da campanha (campaignTypeId), sem definição manual.
            this.isIncentivada =
              config.campaign.campaignTypeId === CampaignTypes.Incentivada;
          }
          this.color = this.themeColorToHex(config.themePrimaria);
          this.colorSecondary = this.themeColorToHex(config.themeSecundaria);
        }
      } catch (e) {
        // silencioso: campanha não resolvida -> segue apenas com o chat
      }
    }

    this.loading = false;

    // Injeta os widgets só depois de resolver o tipo da campanha e renderizar o
    // container do simulador (v-if="isIncentivada").
    this.$nextTick(() => this.loadAgents());
  },
  beforeDestroy() {
    // Remove os scripts injetados para reinicializar limpo ao sair/reentrar
    ["auc-chat-widget-script", "auc-simulador-script"].forEach(id => {
      const el = document.getElementById(id);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    });
    const host = document.getElementById("auc-sim-host");
    if (host && host.parentNode) host.parentNode.removeChild(host);
    window.__aucSimLoaded = undefined;
    window.AUCSimulador = undefined;
  },
  methods: {
    loadAgents() {
      // Chat flutuante: SEMPRE presente (incentivada ou não)
      this.injectChatWidget();
      // Simulador: apenas quando incentivada
      if (this.isIncentivada) this.mountSimulador();
    },
    // Injeta o widget de chat (agente AUC) — mesma lógica da LP de campanha.
    injectChatWidget() {
      if (typeof document === "undefined") return; // guarda SSR
      if (document.getElementById("auc-chat-widget-script")) return; // evita duplicar

      const s = document.createElement("script");
      s.id = "auc-chat-widget-script";
      s.src = "https://chat.abraceumacausa.com.br/webhook/auc-widget-js-v5";
      s.setAttribute(
        "data-webhook",
        "https://chat.abraceumacausa.com.br/webhook/auc-chat-agent-v5"
      );
      if (this.slug) s.setAttribute("data-campaign", this.slug);
      if (this.color) s.setAttribute("data-color", this.color); // primária: bolhas do usuário, foco
      if (this.colorSecondary)
        s.setAttribute("data-color-secondary", this.colorSecondary); // secundária: gradiente cabeçalho/botões
      s.defer = true;
      document.body.appendChild(s);
    },
    // Monta o simulador inline — mesmo padrão da nova página de projeto (ProjectLP).
    mountSimulador() {
      if (document.getElementById("auc-simulador-script")) return;
      const s = document.createElement("script");
      s.id = "auc-simulador-script";
      s.src = "/vendor/auc-simulador-widget.js";
      s.setAttribute("data-mode", "inline");
      s.setAttribute("data-target", "#auc-simulador-inline");
      if (this.slug) s.setAttribute("data-campaign", this.slug);
      // data-campaign-type OMITIDO de propósito: o widget auto-detecta o percentual
      // (6% cultura / 7% esporte) pela campanha; o usuário ainda pode alternar no modal.
      s.setAttribute("data-color", this.color || "#009CA6");
      s.setAttribute("data-color-secondary", this.colorSecondary || "#8CC63F");
      document.body.appendChild(s);
    },
    // Converte a cor do tema (JSON "[r,g,b,a]") para hex "#rrggbb" esperado pelos widgets.
    themeColorToHex(themeValue) {
      try {
        const arr =
          typeof themeValue === "string" ? JSON.parse(themeValue) : themeValue;
        if (!Array.isArray(arr) || arr.length < 3) return null;
        const toHex = function(n) {
          const h = Math.max(0, Math.min(255, Math.round(Number(n)))).toString(16);
          return h.length === 1 ? "0" + h : h;
        };
        return "#" + toHex(arr[0]) + toHex(arr[1]) + toHex(arr[2]);
      } catch (e) {
        return null;
      }
    }
  }
};
</script>

<style scoped>
.teste-agentes {
  min-height: 100vh;
  background: #f5f7f8;
  font-family: "Nunito", "Poppins", system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  color: #13323a;
}
.ta-header {
  background: linear-gradient(135deg, #009ca6 0%, #8cc63f 100%);
  color: #fff;
  padding: 40px 24px 48px;
}
.ta-header-inner {
  max-width: 940px;
  margin: 0 auto;
}
.ta-badge {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.ta-header h1 {
  margin: 14px 0 8px;
  font-size: 30px;
  font-weight: 800;
}
.ta-campaign {
  font-size: 16px;
  margin: 0 0 12px;
}
.ta-tag {
  display: inline-block;
  margin-left: 8px;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 700;
  vertical-align: middle;
}
.ta-tag.is-incentivada {
  background: #fff;
  color: #0a7d4b;
}
.ta-tag.is-nao {
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
}
.ta-warn {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.5;
}
.ta-warn code,
.ta-campaign code {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 5px;
  padding: 1px 6px;
  font-size: 13px;
}
.ta-status {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  font-size: 14px;
}
.ta-status .dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}
.ta-status .dot.on {
  background: #7CFFB2;
  box-shadow: 0 0 0 3px rgba(124, 255, 178, 0.3);
}
.ta-status .dot.off {
  background: rgba(255, 255, 255, 0.4);
}
.ta-main {
  max-width: 940px;
  margin: -24px auto 0;
  padding: 0 24px 64px;
}
.ta-sim,
.ta-only-chat {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(19, 50, 58, 0.08);
  padding: 28px 24px;
}
.ta-sim h2 {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 800;
}
.ta-sim-hint {
  margin: 0 0 20px;
  color: #5b6b70;
  font-size: 14px;
}
.sim-inline-wrap {
  width: 100%;
  max-width: 940px;
  margin: 0 auto;
}
.ta-only-chat p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
}
</style>
